// Matches time-only strings like "11:00", "11:00 AM", "14:30", "2:30 PM"
const TIME_ONLY_REGEX = /^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?$/i;

/**
 * Parses a time-only string and returns a Date object with today's date.
 *
 * @param {string} timeString - Time string like "11:00 AM" or "14:30"
 * @returns {Date|null} Date object or null if parsing fails
 */
const parseTimeString = timeString => {
    const match = timeString.match(TIME_ONLY_REGEX);
    if (!match) return null;

    let [, hours, minutes, , period] = match;
    hours = parseInt(hours, 10);
    minutes = parseInt(minutes, 10);

    // Convert 12h to 24h format if AM/PM is present
    if (period) {
        const isPM = period.toUpperCase() === 'PM';
        if (isPM && hours !== 12) hours += 12;
        if (!isPM && hours === 12) hours = 0;
    }

    const dateObj = new Date();
    dateObj.setHours(hours, minutes, 0, 0);
    return dateObj;
};

/**
 * Formats a date using browser's Intl.DateTimeFormat for locale-aware display.
 * Automatically formats as EU (21 Jan 2026) or US (Jan 21, 2026) based on browser locale.
 *
 * @param {string|Date|number} date - The date to format, or a time-only string when onlyTime is true
 * @param {Object} [options] - Configuration options
 * @param {boolean} [options.includeTime=false] - Whether to include time in date output
 * @param {boolean} [options.onlyTime=false] - When true, treats input as time-only string (e.g., "11:00 AM")
 * @param {string} [options.timezone] - IANA timezone (e.g., "America/New_York").
 *                                       Defaults to browser's local timezone.
 * @returns {string} Formatted date string, or '-' if date is falsy or invalid
 */
export const formatDate = (date, options = {}) => {
    if (!date) return '-';

    const { includeTime = false, onlyTime = false, timezone } = options;

    // Handle time-only input
    if (onlyTime) {
        const dateObj = typeof date === 'string' ? parseTimeString(date.trim()) : null;

        if (!dateObj) return '-';

        const formatOptions = {
            hour: 'numeric',
            minute: '2-digit',
            ...(timezone && { timeZone: timezone })
        };

        return new Intl.DateTimeFormat(undefined, formatOptions).format(dateObj);
    }

    const dateObj = new Date(date);

    if (isNaN(dateObj.getTime())) return '-';

    const formatOptions = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        ...(timezone && { timeZone: timezone }),
        ...(includeTime && {
            hour: 'numeric',
            minute: '2-digit'
        })
    };

    return new Intl.DateTimeFormat(undefined, formatOptions).format(dateObj);
};
